import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PredefinedDateRanges from "../../../core/common/datePicker";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import Table from "../../../core/common/dataTable/index";
import { all_routes } from "../../router/all_routes";
import { paymentReport } from "../../../core/data/json/paymentReport";
import ReactApexChart from "react-apexcharts";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { formatDate } from "../../../core/utils/FormatData";
import { fetchAllAssets } from "../../../core/data/redux/asset/asset/assetActions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchAllMaintenances } from "../../../core/data/redux/asset/assetMaintenanceAction/assetMaintenanceAction";
import {
  fetchAllMaintenanceAmountReport,
  fetchAllMaintenanceAmountReportMonthly,
  fetchAllUpcomingMaintenance,
} from "../../../core/data/redux/reports/maintenance_report/maintenance.Action";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import {
  exportToCSV,
  exportToExcel,
} from "../../../core/common/GlobalFunctions/exportTableData";


interface AssetMaintainceProps {
  showOnlyTable?: boolean;
}
const AssetMaintainceReport: React.FC<AssetMaintainceProps> = ({ showOnlyTable}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [tableKey, setTableKey] = useState(0);
  const [upcoming, setupcoming] = useState<any[]>([]);
  const [selectedStatus, setSelectedStatus] = useState("Pending");
  const [selectedSort, setSelectedSort] = useState<any>("Recently Added");
  const [amount, setamount] = useState<any>(null);
  const [selectedYear, setSelectedYear] = useState("2025");
  const [count, setcount] = useState<any>();
  const { maintenancereport } = useSelector(
    (state: any) => state.report.maintenancereport
  );
  const { maintenances = [] } = useSelector(
    (state: any) => state.asset.assetMaintenance
  );
  const { assetSummary, assets, assetLogs, loading } = useSelector(
    (state: any) => state.asset.newAsset
  );

  
  const simplifiedAssets = useMemo(() => {
  return (
    assets?.reduce((result: any[], asset: any) => {
      const matchedMaintenance = maintenances?.find(
        (maintenance: any) => maintenance.asset_id === asset.assetId
      );
      if (!matchedMaintenance) return result;

      result.push({
        asset_name: asset?.AssetName || " ",
        total_quantity: asset?.Quantity || 0,
        available_quantity: asset?.availableAssetCount || 0,
        serialNumber: asset?.assetDetails?.[0]?.SerialNumber || " ",
        type: matchedMaintenance.maintenance_type || " ",
        cost: matchedMaintenance.cost || 0,
        status: matchedMaintenance.status || " ",
        date: matchedMaintenance.maintenance_date || " ",
        nextDueDate: matchedMaintenance.next_due_date || " ",
      });

      return result;
    }, []) || []
  );
}, [assets, maintenances]);


  const data = simplifiedAssets ? simplifiedAssets : [];

  const filteredData = useMemo(() => {
  return filterData(data || [], {}, "CreatedAt", "CreatedAt", selectedSort);
}, [data, selectedSort]);

useEffect(() => {
  setTableKey((prevKey) => prevKey + 1);
}, [selectedSort, selectedYear, data]);


  const columns = [
    {
      title: "Asset",
      dataIndex: "asset_name",
      render: (text: string) => text?.charAt(0).toUpperCase() + text?.slice(1),
    },
    {
      title: "Total Quantity",
      dataIndex: "total_quantity",
      sorter: (a: any, b: any) => a.total_quantity - b.total_quantity,
    },
    {
      title: "Serial Number",
      dataIndex: "serialNumber",
      sorter: (a: any, b: any) => a.serialNumber - b.serialNumber,
    },
    {
      title: "Available Quantity",
      dataIndex: "available_quantity",
      sorter: (a: any, b: any) => a.available_quantity - b.available_quantity,
    },
    {
      title: "Maintenance Type",
      dataIndex: "type",
      render: (type: string) => type?.charAt(0).toUpperCase() + type?.slice(1),
    },
    {
      title: "Mainentance Date",
      dataIndex: "date",
      render: formatDate,
      sorter: (a: any, b: any) =>
        new Date(a.date).getTime() - new Date(b.date).getTime(),
    },
    {
      title: "Cost",
      dataIndex: "cost",
      sorter: (a: any, b: any) => a.cost - b.cost,
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: (a: any, b: any) => a.request_status - b.request_status,
    },
  ];

  useEffect(() => {
    dispatch(fetchAllAssets());
    dispatch(fetchAllMaintenances());
    dispatch(fetchAllUpcomingMaintenance()).then((res) => {
      setupcoming(res.payload.data);
    });
  }, []);

  useEffect(() => {
    if (count && count.length === 12) {
      const monthlyCosts = count.map(
        (item: any) => parseFloat(item.total_completed_cost) || 0
      );

      const maxValue = Math.max(...monthlyCosts);
      const yAxisMax = Math.ceil(maxValue / 1000) * 1000 || 1000;

      setmaintenancechart((prev: any) => ({
        ...prev,
        series: [
          {
            ...prev.series[0],
            data: monthlyCosts,
          },
        ],
        yaxis: {
          ...prev.yaxis,
          max: yAxisMax,
          tickAmount: 5,
          labels: {
            offsetX: -15,
            formatter: (val: number) => {
              if (val >= 100000) return (val / 100000).toFixed(1) + "L";
              if (val >= 1000) return (val / 1000).toFixed(1) + "K";
              return val;
            },
          },
        },
      }));
    }
  }, [count]);

  useEffect(() => {
    if (selectedYear) {
      dispatch(fetchAllMaintenanceAmountReport(selectedYear)).then((res) => {
        setamount(res.payload.data);
      });
      dispatch(fetchAllMaintenanceAmountReportMonthly(selectedYear))
        .then((res) => {
          setcount(res.payload.data);
        })
        .catch((err: any) => {
          console.error("Failed to fetch payslip data:", err);
        });
    }
  }, [selectedYear]);

  const handleYearSelect = (year: any) => {
    setSelectedYear(year);
  };

  const [maintenancechart, setmaintenancechart] = useState<any>({
    series: [
      {
        name: "Maintenance Costs",
        data: new Array(12).fill(0),
      },
    ],
    chart: {
      height: 190,
      type: "area",
      zoom: {
        enabled: false,
      },
    },
    colors: ["#FF9F43"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
    },
    title: {
      text: "",
      align: "left",
    },
    // grid: {
    //   row: {
    //     colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
    //     opacity: 0.5
    //   },
    // },
    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
    },
    yaxis: {
      min: 0,
      max: 6500,
      tickAmount: 5,
      labels: {
        offsetX: -15,
        formatter: (val: number) => {
          return val / 1 + "K";
        },
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
    },
  });

    if (showOnlyTable) {
  return (
  <Table
   key={tableKey}
   dataSource={filteredData}
   columns={columns}
   Selection={true}
 />
  );
}
  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Asset Maintenance Report</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">HR</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Asset Maintenance
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="mb-2">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-file-export me-1" />
                    Export
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <button
                        onClick={() =>
                          exportToCSV(data, "Asset_Maintenance_report")
                        }
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-pdf me-1" />
                        Export as CSV
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() =>
                          exportToExcel(data, "Asset_Maintenance_report")
                        }
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-xls me-1" />
                        Export as Excel{" "}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 d-flex flex-wrap">
              <div className="col-lg-3 col-md-6 d-flex">
                <div className="card flex-fill">
                  <div className="card-body ">
                    <div className="d-flex flex-wrap align-items-center justify-content-between border-bottom pb-2">
                      <div className="d-flex align-items-center flex-column overflow-hidden">
                        <div>
                          <div>
                            <span className="fs-14 fw-normal text-truncate mb-1">
                              Total Maintenance Cost
                            </span>
                            <h5>{amount?.[0].total_maintenance_cost}</h5>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <Link
                          to="#"
                          className="avatar avatar-md br-5 payment-report-icon  bg-transparent-primary border border-primary"
                        >
                          <span className="text-primary">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                    </div>
                    <div className="d-flex justify-content-center mt-2"></div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 d-flex">
                <div className="card flex-fill">
                  <div className="card-body ">
                    <div className="d-flex flex-wrap align-items-center justify-content-between border-bottom pb-2">
                      <div className="d-flex align-items-center flex-column overflow-hidden">
                        <div>
                          <div>
                            <span className="fs-14 fw-normal text-truncate mb-1">
                              Paid Payment
                            </span>
                            <h5>{amount?.[0].total_maintenance_cost}</h5>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <Link
                          to="#"
                          className="avatar avatar-md br-5 payment-report-icon  bg-transparent-skyblue border border-skyblue"
                        >
                          <span className="text-skyblue">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                    </div>
                    <div className="d-flex justify-content-center mt-2"></div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 d-flex">
                <div className="card flex-fill">
                  <div className="card-body ">
                    <div className="d-flex flex-wrap align-items-center justify-content-between border-bottom pb-2">
                      <div className="d-flex align-items-center flex-column overflow-hidden">
                        <div>
                          <div>
                            <span className="fs-14 fw-normal text-truncate mb-1">
                              Unpaid Payment
                            </span>
                            <h5>{amount?.[0].total_unpaid_cost}</h5>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <Link
                          to="#"
                          className="avatar avatar-md br-5 payment-report-icon  bg-transparent-danger border border-danger"
                        >
                          <span className="text-danger">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                    </div>
                    {/* <div className="d-flex justify-content-center mt-2"> */}
                    {/* <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                                                    <span className="text-danger fs-12 d-flex align-items-center me-1">
                                                        <i className="ti ti-arrow-wave-right-up me-1" />
                                                        +20.01%
                                                    </span>{" "}
                                                    from last week
                                                </p> */}
                    {/* </div> */}
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 d-flex">
                <div className="card flex-fill">
                  <div className="card-body ">
                    <div className="d-flex flex-wrap align-items-center justify-content-between border-bottom pb-2">
                      <div className="d-flex align-items-center flex-column overflow-hidden">
                        <div>
                          <div>
                            <span className="fs-14 fw-normal text-truncate mb-1">
                              Total Maintenance Units
                            </span>
                            <h5>{amount?.[0]?.total_maintenance_count}</h5>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <Link
                          to="#"
                          className="avatar avatar-md br-5 payment-report-icon  bg-pink-transparent border border-pink"
                        >
                          <span className="text-pink">
                            <i className="ti ti-tools" />
                          </span>
                        </Link>
                      </div>
                    </div>
                    <div className="d-flex justify-content-center mt-2">
                      {/* <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                                                    <span className="text-success fs-12 d-flex align-items-center me-1">
                                                        <i className="ti ti-arrow-wave-right-up me-1" />
                                                        +20.01%
                                                    </span>{" "}
                                                    from last week
                                                </p> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ---- ROW 2: Chart ---- */}
            <div className="row mt-4">
              {/* First Chart */}
              <div className="col-md-6">
                <div className="card flex-fill">
                  <div className="card-header border-0">
                    <div className="d-flex align-items-center">
                      <p className="d-inline-flex align-items-center me-2 mb-0">
                        <i
                          className="ti ti-square-filled fs-12  me-2"
                          style={{ color: "#FF9F43" }}
                        />
                        Maintenance Cost Chart
                      </p>
                      <div className="dropdown">
                        <Link
                          to="#"
                          className="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                          data-bs-toggle="dropdown"
                        >
                          {selectedYear}
                        </Link>
                        <ul className="dropdown-menu  dropdown-menu-end p-2">
                          <li>
                            <Link
                              to="#"
                              className="dropdown-item rounded-1"
                              onClick={() => handleYearSelect("2025")}
                            >
                              2025
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="#"
                              className="dropdown-item rounded-1"
                              onClick={() => handleYearSelect("2024")}
                            >
                              2024
                            </Link>
                          </li>
                          <li>
                            <Link
                              to="#"
                              className="dropdown-item rounded-1"
                              onClick={() => handleYearSelect("2023")}
                            >
                              2023
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="card-body pt-0">
                    <ReactApexChart
                      id="expense-analysis"
                      options={maintenancechart}
                      series={maintenancechart.series}
                      type="area"
                      height={210}
                    />
                  </div>
                </div>
              </div>

              {/* Second Chart */}
              <div className="col-md-6">
                <div className="card flex-fill">
                  <div className="card-header border-0">
                    <div className="d-flex align-items-center">
                      <p className="d-inline-flex align-items-center me-2 mb-0">
                        <i className="ti ti-square-filled fs-12 text-success me-2" />
                        Upcoming Maintenance
                      </p>
                    </div>
                  </div>
                  <div
                    className="card-body pt-0"
                    style={{ maxHeight: "210px", overflowY: "auto" }}
                  >
                    <ul className="timeline-list p-0 m-0">
                      {Array.isArray(upcoming) &&
                        upcoming.map((item: any, index: number) => {
                          const date = new Date(item.maintenance_date);
                          const formattedDate = `${String(
                            date.getDate()
                          ).padStart(2, "0")}/${String(
                            date.getMonth() + 1
                          ).padStart(2, "0")}/${date.getFullYear()}`;

                          return (
                            <li
                              key={index}
                              className="mb-2 p-3 rounded border d-flex flex-column bg-light shadow-sm"
                            >
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold">
                                  🛠️ {formattedDate}
                                </span>
                                <span className="text-primary text-capitalize">
                                  {item.maintenance_type}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between text-muted small">
                                <span>{item.AssetName}</span>
                                <span>SN: {item.SerialNumber}</span>
                              </div>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Asset Maintenance List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="me-3"></div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedSort
                      ? `Sort By : ${selectedSort}`
                      : "Sort By : Last 7 Days"}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Recently Added")}
                        className="dropdown-item rounded-1"
                      >
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Ascending")}
                        className="dropdown-item rounded-1"
                      >
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Descending")}
                        className="dropdown-item rounded-1"
                      >
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last Month")}
                        className="dropdown-item rounded-1"
                      >
                        Last Month
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last 7 Days")}
                        className="dropdown-item rounded-1"
                      >
                        Last 7 Days
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={columns}
                Selection={true}
              />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default AssetMaintainceReport;
